import { Component, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { AssetListItem, TaskResponse } from '@key-telematics/fleet-api-client';
import { AppService } from 'app/app.service';
import { ErrorLoggerService } from 'app/services';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'key-overview-details-task',
    templateUrl: './details-task.component.html',
})
export class OverviewDetailsTaskComponent implements OnChanges, OnDestroy {
    tasks: TaskResponse[] = [];
    page = 1;
    count = 0;
    errorMessage: string;
    limit = 10;
    listLoaded = false;

    timer: NodeJS.Timeout;

    @Input() asset: AssetListItem;
    @Input() isVisible: boolean;

    get pageCount(): number {
        return (this.count / this.limit);
    }

    constructor(
        private app: AppService, 
        private error: ErrorLoggerService,
        private i18n: TranslateService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes && changes.asset) {
            this.getTasks(0);
        }

        if (this.isVisible) {
            clearInterval(this.timer);
            this.timer = setInterval(() => {
                this.getTasks(0);
            }, 30e3);
        } else {
            clearInterval(this.timer);
        }
    }

    ngOnDestroy(): void {
        clearInterval(this.timer);
    }

    async getTasks(offset: number) {
        try {
            this.listLoaded = false;
            const taskList = await this.app.api.tasks.listTasks(this.asset.owner.id, offset, this.limit || 100, '', `linked=${this.asset.id}`);
            this.tasks = taskList.items || [];
            this.count = taskList.count;
            this.listLoaded = true;
        } catch (error) {
            this.showError(error);
        }
        
    }

    async onPageChanged(page: number) {
        this.page = page;
        const offset = (page - 1) * this.limit || 0;
        await this.getTasks(offset);
    }

    getDescription(task: TaskResponse) {

        switch (task.type) {
            case 'DeviceTextMessageTask':
                return this.i18n.instant(`STATUS.DETAILS.TASK_DESC.DEVICE_TEXT_MESSAGE`, {
                    user: task.user?.name,
                    message: task.data.message.message,
                    status: task.state,
                });
            case 'DeviceDialbackTask':
                return this.i18n.instant(`STATUS.DETAILS.TASK_DESC.DEVICE_DIALBACK`, {
                    user: task.user?.name,
                    number: task.data.dialback.number,
                    status: task.state,
                });
            case 'DeviceAddressMessageTask':
                return this.i18n.instant(`STATUS.DETAILS.TASK_DESC.DEVICE_ADDRESS_MESSAGE`, {
                    user: task.user?.name,
                    message: task.data.message.message,
                    lat: task.data.message.lat,
                    lon: task.data.message.lon,
                    status: task.state,
                });
            case 'DevicePollTask':
                return this.i18n.instant(`STATUS.DETAILS.TASK_DESC.DEVICE_POLL`, {
                    user: task.user?.name,
                    status: task.state,
                });
            case 'DeviceOutputTask':
                return this.i18n.instant(`STATUS.DETAILS.TASK_DESC.DEVICE_OUTPUT`, {
                    user: task.user?.name,
                    output: task.data?.output?.name,
                    value: task.data?.output?.value.toUpperCase(),
                    status: task.state,
                });
            case 'DeviceUserQueryTask':
                return this.i18n.instant(`STATUS.DETAILS.TASK_DESC.DEVICE_USER_QUERY`, {
                    user: task.user?.name,
                    command: task.data?.query?.cmd,
                    status: task.state,
                });
        }

        if (task.data?.camera) {
            return this.i18n.instant(`STATUS.DETAILS.TASK_DESC.MEDIA`, {
                camera: task.data.camera.output,
                status: task.state,
            });
        }

        if (task.data?.output) {
            // This was added before reworking this method ... this should be killed when all translations have been added for DeviceOutputTask
            return this.i18n.instant(`STATUS.DETAILS.TASK_DESC.OUTPUT`, {
                output: task.data?.output?.name,
                name: task.user?.name,
                value: task.data?.output?.value.toUpperCase(),
                status: task.state,
            });
        }

        if (task.user) {
            return this.i18n.instant(`STATUS.DETAILS.TASK_DESC.DEFAULT_BY_USER`, {
                user: task.user?.name,
                status: task.state,
            });
        }

        return this.i18n.instant(`STATUS.DETAILS.TASK_DESC.DEFAULT`, {
            status: task.state,
        });
    }

    showError(error: any) {
        this.error.trackException(error);
        this.errorMessage = error ? error.message || error.error || error : null;
    }
}
